import { Component, type ErrorInfo, type ReactNode, Suspense, useEffect } from 'react';
import { RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import i18n from './i18n';
import { router } from './routes';
import { AuthInitializer } from './components/auth/AuthInitializer';
import { PortalAuthInitializer } from './components/portal/PortalAuthInitializer';
import { BrandingInitializer } from './components/branding/BrandingInitializer';
import { PageSkeleton } from './components/shared/PageSkeleton';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

/**
 * BUG-E14: Sync <html lang> with the active i18n language so that HTML5
 * <input type="date"> honours the correct locale format (dd/mm/yyyy for "es",
 * mm/dd/yyyy for "en").  Browsers determine date-input display from the
 * document language, not the app's JavaScript locale.
 */
function HtmlLangSync() {
  useEffect(() => {
    const sync = (lng: string) => {
      document.documentElement.lang = lng.split('-')[0]; // "es-ES" → "es"
    };
    sync(i18n.language);
    i18n.on('languageChanged', sync);
    return () => {
      i18n.off('languageChanged', sync);
    };
  }, []);
  return null;
}

/**
 * Root-level error boundary — last resort catch for errors that escape all
 * route-level boundaries (e.g. errors thrown during routing itself or inside
 * the QueryClientProvider). Shows a minimal reload prompt instead of a blank
 * white screen or browser chrome-error page.
 */
class RootErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[RootErrorBoundary] uncaught error:', error, info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          role="alert"
          className="flex min-h-screen flex-col items-center justify-center gap-4 p-8 text-center"
        >
          <p className="text-[15px] font-semibold text-ink">
            The application encountered an unexpected error.
          </p>
          <p className="max-w-sm text-[12.5px] text-muted-foreground">
            This is likely a temporary issue. Reload the page to recover.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="mt-2 rounded-md bg-primary px-4 py-2 text-[13px] font-medium text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          >
            Reload page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  return (
    <RootErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <HtmlLangSync />
        <BrandingInitializer>
          <AuthInitializer>
            <PortalAuthInitializer>
              {/*
                #475 — Global Suspense boundary: prevents blank white screens during
                route transitions or when lazy-loaded modules/i18n resources stall.
                PageSkeleton preserves sidebar + header chrome so the app never
                shows a completely blank canvas.
              */}
              <Suspense fallback={<PageSkeleton />}>
                {/* CONS-N03: v7_startTransition — wrapping router-driven state
                    updates in React.startTransition — became the only behavior
                    in React Router v7 at the CEO-D-300 upgrade (2026-08-22); the
                    `future` prop is gone from `RouterProvider` because there is
                    no longer an opt-in to make (see routes/index.tsx for the
                    matching router-config flags removal). */}
                <RouterProvider router={router} />
              </Suspense>
            </PortalAuthInitializer>
          </AuthInitializer>
        </BrandingInitializer>
      </QueryClientProvider>
    </RootErrorBoundary>
  );
}

export default App;
